import Big from 'big.js'
import { formatFiatCurrency } from './currency/formatFiatCurrency'
import { extractDynamicDecimalPlaces } from './currency/extractDynamicDecimalPlaces'
import { cryptoToBig } from './currency/crypto'
import { removeTrailingZeros } from './currency/common'
import {
  FIAT_CODES,
  STABLECOIN_CODES,
  CRYPTO_CODES,
} from '@/constants/currencyCodes'

export const currencyUtils = {
  formatFiatCurrency: ({
    value,
    fiatCode = 'USD',
    showDecimals = true,
  }: {
    value: number
    fiatCode?: string
    showDecimals?: boolean
  }): string => {
    return formatFiatCurrency({
      value,
      fiatCode,
      showDecimals,
    })
  },

  formatDynamicCurrency: ({
    amount,
    currencyCode,
    showDecimals = false,
    shouldMaskStablecoinAsFiat = false,
  }: {
    amount: string | number
    currencyCode: string
    showDecimals?: boolean
    shouldMaskStablecoinAsFiat?: boolean
  }): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount

    if (isNaN(numAmount)) return '0'

    const code = currencyCode.toUpperCase()

    // Fiat currencies
    if (FIAT_CODES.includes(code as any)) {
      return currencyUtils.formatFiatCurrency({
        value: numAmount,
        fiatCode: code,
        showDecimals,
      })
    }

    // Stablecoins - if shouldMaskStablecoinAsFiat = true, display as dollars
    const stablecoins = ['USDT', 'KWDK', 'AEDK', 'SARK']
    if (stablecoins.includes(code)) {
      if (shouldMaskStablecoinAsFiat) {
        // Display as dollars
        return currencyUtils.formatFiatCurrency({
          value: numAmount,
          fiatCode: 'USD',
          showDecimals: true,
        })
      }

      // Format as cryptocurrency with currency code
      const formattedAmount = showDecimals
        ? numAmount.toFixed(6).replace(/\.?0+$/, '') // Remove trailing zeros
        : numAmount.toString()

      return `${formattedAmount} ${code}`
    }

    // Bitcoin
    if (code === 'BTC') {
      return `₿${numAmount.toFixed(8).replace(/\.?0+$/, '')}`
    }

    // Gold (XAUT)
    if (code === 'XAUT') {
      return `${numAmount.toFixed(6).replace(/\.?0+$/, '')} GOLD`
    }

    // Ethereum
    if (code === 'ETH') {
      return `${numAmount.toFixed(8).replace(/\.?0+$/, '')} ETH`
    }

    // Other cryptocurrencies
    const formattedAmount = showDecimals
      ? numAmount.toFixed(8).replace(/\.?0+$/, '')
      : numAmount.toString()

    return `${formattedAmount} ${code}`
  },

  cryptoToFiat: ({
    cryptoPricePerUnit,
    amountCrypto,
    keepFullPrecision = false,
    precision = 2,
  }: {
    cryptoPricePerUnit: number
    amountCrypto: number
    keepFullPrecision?: boolean
    precision?: number
  }): string => {
    if (!cryptoPricePerUnit || amountCrypto <= 0) return '0'

    const priceBig = new Big(cryptoPricePerUnit)
    const amountBig = new Big(amountCrypto)
    const amountInFiat = priceBig.times(amountBig)

    if (keepFullPrecision) {
      return amountInFiat.toString()
    }

    return amountInFiat.toFixed(precision)
  },

  fiatToCrypto: ({
    relativeFiatAmount,
    cryptoPricePerUnitInFiat,
    keepFullPrecision = false,
  }: {
    relativeFiatAmount: string | number
    cryptoPricePerUnitInFiat: number
    keepFullPrecision?: boolean
  }): string => {
    if (!cryptoPricePerUnitInFiat || !relativeFiatAmount) {
      return '0'
    }

    const unitPrice =
      typeof cryptoPricePerUnitInFiat === 'string'
        ? currencyUtils.parseAmountToFloat({
            amount: cryptoPricePerUnitInFiat,
            decimalSeparator: '.',
          })
        : cryptoPricePerUnitInFiat

    const fiatAmount =
      typeof relativeFiatAmount === 'string'
        ? currencyUtils.parseAmountToFloat({
            amount: relativeFiatAmount,
            decimalSeparator: '.',
          })
        : relativeFiatAmount

    if (
      !Number.isFinite(unitPrice) ||
      unitPrice <= 0 ||
      !Number.isFinite(fiatAmount)
    ) {
      return '0'
    }

    const fiatBig = new Big(fiatAmount)
    const priceBig = new Big(unitPrice)
    const rawCryptoAmount = fiatBig.div(priceBig)

    return cryptoToBig({
      amount: rawCryptoAmount.toString(),
      keepFullPrecision,
    })
  },

  parseAmountToFloat: ({
    amount,
    decimalSeparator = '.',
  }: {
    amount: string
    decimalSeparator?: string
  }): number => {
    if (!amount) return 0

    // Replace separator with dot for parseFloat
    const normalizedAmount =
      decimalSeparator !== '.' ? amount.replace(decimalSeparator, '.') : amount

    const parsed = parseFloat(normalizedAmount)
    return Number.isNaN(parsed) ? 0 : parsed
  },

  currencyConditionals: (code: string) => {
    const normalizedCode = code?.toUpperCase()

    return {
      isUsdt: normalizedCode === 'USDT',
      isBtc: normalizedCode === 'BTC',
      isEth: normalizedCode === 'ETH',
      isGold: normalizedCode === 'XAUT',
      isStablecoin: STABLECOIN_CODES.includes(normalizedCode as any),
      isNewStableCoins: ['KWDK', 'AEDK', 'SARK'].includes(normalizedCode),
      isCrypto: CRYPTO_CODES.includes(normalizedCode as any),
      isFiduciary: FIAT_CODES.includes(normalizedCode as any),
      isDollar: normalizedCode === 'USD',
    }
  },

  extractDynamicDecimalPlaces,

  convertBetweenCurrencies: ({
    amount,
    rateFromCurrency,
    rateToCurrency,
    toCurrency,
  }: {
    amount: number
    rateFromCurrency: number
    rateToCurrency: number
    toCurrency?: string
  }): string => {
    if (isNaN(amount) || amount === 0) {
      return '0'
    }

    if (rateFromCurrency === 0 || rateToCurrency === 0) return '0'

    const amountBig = new Big(amount)
    const rateFromBig = new Big(rateFromCurrency)
    const rateToBig = new Big(rateToCurrency)

    // Step 1: Conversion to USD
    const amountInUSD = amountBig.times(rateFromBig)

    // Step 2: Conversion from USD to the target currency
    const conversionFromUSD = new Big(1).div(rateToBig)
    let convertedAmount = amountInUSD.times(conversionFromUSD)

    // If conversion is to USD, round to the nearest integer
    if (toCurrency === 'USD') {
      convertedAmount = new Big(Math.round(convertedAmount.toNumber()))
    }

    // Format based on currency type with better precision for small amounts
    const precision = convertedAmount.lt(0.00001) ? 12 : 8

    if (toCurrency === 'BTC' || toCurrency === 'ETH') {
      const formatted = convertedAmount.toFixed(precision)
      const trimmed = removeTrailingZeros(formatted)
      return trimmed === '' || trimmed === '0'
        ? convertedAmount.toString()
        : trimmed
    } else if (toCurrency === 'XAUT') {
      return removeTrailingZeros(convertedAmount.toFixed(6))
    } else if (['USDT', 'USDC', 'USD'].includes(toCurrency || '')) {
      return convertedAmount.toFixed(2)
    }

    const formatted = convertedAmount.toFixed(precision)
    const trimmed = removeTrailingZeros(formatted)
    return trimmed === '' || trimmed === '0'
      ? convertedAmount.toString()
      : trimmed
  },

  removeTrailingZeros,

  cryptoToBig,

  formatCryptoAmount: ({
    value,
    cryptoCode = 'USDT',
    showDecimals = true,
    showCryptoCode = true,
  }: {
    value: number
    cryptoCode?: string
    showDecimals?: boolean
    showCryptoCode?: boolean
  }): string => {
    if (!showDecimals) {
      return `${Math.floor(value)} ${cryptoCode}`
    }

    // For crypto currencies we typically show up to 8 decimal places
    const decimals = value < 1 ? 8 : 4
    return `${value.toFixed(decimals).replace(/\.?0+$/, '')} ${showCryptoCode ? cryptoCode : ''}`
  },
}

export const capitalizeFirstLetterAllWords = (str: string): string => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}
